import { useEffect, useRef, useState } from "react";

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function webglOK() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch {
    return false;
  }
}

const StaticGift = ({ hidden }) => (
  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${hidden ? "opacity-0" : "opacity-100"}`} data-testid="gift-static">
    <div className="absolute inset-[12%] rounded-full bg-[radial-gradient(circle,rgba(245,174,61,0.45)_0%,rgba(245,174,61,0.12)_45%,transparent_70%)]" />
    <svg viewBox="0 0 200 200" className="relative w-[62%]" role="presentation">
      <ellipse cx="100" cy="178" rx="70" ry="9" fill="#24453D" opacity="0.14" />
      <rect x="38" y="84" width="124" height="92" rx="8" fill="#C73450" />
      <rect x="32" y="66" width="136" height="26" rx="6" fill="#A82A43" />
      <rect x="92" y="66" width="16" height="110" fill="#F5AE3D" />
      <path d="M100 66 C 78 40, 52 46, 62 62 C 68 70, 88 68, 100 66 Z" fill="#F5AE3D" />
      <path d="M100 66 C 122 40, 148 46, 138 62 C 132 70, 112 68, 100 66 Z" fill="#F5AE3D" />
      <circle cx="100" cy="66" r="8" fill="#E39A22" />
    </svg>
  </div>
);

export function GiftSection() {
  const sectionRef = useRef(null);
  const hostRef = useRef(null);
  const [is3d, setIs3d] = useState(false);

  useEffect(() => {
    if (reducedMotion() || !webglOK() || !("IntersectionObserver" in window)) return undefined;
    let cancelled = false;
    let cleanup;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      import("@/components/site/giftScene")
        .then((m) => {
          if (cancelled) return;
          cleanup = m.mountGift(hostRef.current, sectionRef.current, () => setIs3d(true), () => setIs3d(false));
        })
        .catch(() => setIs3d(false));
    }, { rootMargin: "600px 0px" });
    io.observe(sectionRef.current);
    return () => { cancelled = true; io.disconnect(); cleanup?.(); };
  }, []);

  return (
    <section ref={sectionRef} aria-labelledby="gift-heading" className="of-container py-12 sm:py-20" data-testid="gift-section">
      <div className="grid items-center gap-6 md:grid-cols-[1fr_1.1fr] md:gap-12">
        <div>
          <p className="of-eyebrow">The moment it's opened</p>
          <h2 id="gift-heading" className="mt-3 text-3xl font-semibold sm:text-4xl">Every gift is opened by a child who needed a good day.</h2>
          <p className="mt-3 text-lg text-[#3B4A44]">Your toy, your dollar, your time: it all ends here, in a small moment of joy.</p>
        </div>
        <div className="relative mx-auto aspect-square w-full max-w-[480px]" aria-hidden="true" data-mode={is3d ? "3d" : "static"} data-testid="gift-stage">
          <StaticGift hidden={is3d} />
          <div ref={hostRef} className={`absolute inset-0 transition-opacity duration-500 ${is3d ? "opacity-100" : "opacity-0"}`} data-testid="gift-canvas-host" />
        </div>
      </div>
    </section>
  );
}
